import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "../lib/api";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Select } from "../components/ui/Select";
import { Textarea } from "../components/ui/Textarea";
import { REPORT_TYPES, URGENCY_LEVELS } from "../lib/types";
import type { Organization } from "../lib/types";

interface FormState {
  title: string;
  report_type: string;
  location: string;
  neighborhood: string;
  incident_datetime: string;
  description: string;
  urgency: string;
  organization_id: string;
  anonymous: boolean;
}

const initial: FormState = {
  title: "",
  report_type: "suspicious_activity",
  location: "",
  neighborhood: "",
  incident_datetime: "",
  description: "",
  urgency: "low",
  organization_id: "",
  anonymous: false,
};

export default function SubmitReport() {
  const api = useApi();
  const nav = useNavigate();
  const [form, setForm] = useState<FormState>(initial);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ organizations: Organization[] }>("/api/organizations")
      .then((r) => setOrgs(r.organizations))
      .catch(() => setOrgs([]));
  }, [api]);

  const update =
    <K extends keyof FormState>(key: K) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => {
      const value =
        key === "anonymous"
          ? (e.target as HTMLInputElement).checked
          : e.target.value;
      setForm((s) => ({ ...s, [key]: value }) as FormState);
    };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/reports", {
        title: form.title.trim(),
        report_type: form.report_type,
        location: form.location || null,
        neighborhood: form.neighborhood || null,
        incident_datetime: form.incident_datetime || null,
        description: form.description,
        urgency: form.urgency,
        anonymous: form.anonymous,
        organization_id: form.organization_id
          ? Number(form.organization_id)
          : null,
      });
      nav("/my-reports");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-page py-12">
      <div className="mx-auto max-w-2xl">
        <h1 className="text-3xl font-semibold text-white">Submit a report</h1>
        <p className="mt-3 text-slate-300">
          Reports do not automatically become public alerts. Trusted verifiers
          and a platform admin review every submission. Personal details are
          removed from any public alert.
        </p>

        <form className="surface mt-8 grid gap-5 p-6" onSubmit={submit}>
          <Input
            required
            label="Title"
            name="title"
            value={form.title}
            onChange={update("title")}
            placeholder="Short, factual summary (no names or addresses)"
            maxLength={200}
            hint="One line. Avoid personal identifying info — admins use this for triage."
          />

          <Select
            label="Report type"
            name="report_type"
            value={form.report_type}
            onChange={update("report_type")}
          >
            {REPORT_TYPES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Select>

          <div className="grid gap-5 sm:grid-cols-2">
            <Input
              label="Location"
              name="location"
              value={form.location}
              onChange={update("location")}
              placeholder="Street, intersection, or landmark"
            />
            <Input
              label="Neighborhood"
              name="neighborhood"
              value={form.neighborhood}
              onChange={update("neighborhood")}
            />
          </div>

          <Input
            type="datetime-local"
            label="Date / time of incident"
            name="incident_datetime"
            value={form.incident_datetime}
            onChange={update("incident_datetime")}
          />

          <Select
            label="Urgency"
            name="urgency"
            value={form.urgency}
            onChange={update("urgency")}
          >
            {URGENCY_LEVELS.map((u) => (
              <option key={u} value={u}>
                {u[0].toUpperCase() + u.slice(1)}
              </option>
            ))}
          </Select>

          <Select
            label="Organization affected (optional)"
            name="organization_id"
            value={form.organization_id}
            onChange={update("organization_id")}
          >
            <option value="">— None —</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} ({o.type.replace("_", " ")})
              </option>
            ))}
          </Select>

          <Textarea
            required
            label="Description"
            name="description"
            value={form.description}
            onChange={update("description")}
            rows={6}
            hint="What happened, who/what was involved, and anything that helps verify it."
          />

          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input
              type="checkbox"
              checked={form.anonymous}
              onChange={update("anonymous")}
              className="h-4 w-4 rounded border-white/20 bg-ink-900 text-accent focus:ring-accent/30"
            />
            Submit this report anonymously (your account is not stored on the
            report)
          </label>

          <div className="surface-soft p-4 text-xs text-slate-400">
            Evidence / photo upload is on the roadmap — this MVP does not yet
            accept file uploads. For now, describe evidence in the report and
            keep originals secure.
          </div>

          {error && (
            <p className="rounded-md border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </p>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Submitting…" : "Submit report"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
